import React from "react";
import { useBrand } from "@/context/BrandContext";
import { Truck } from "lucide-react";

/**
 * Logo — single source of truth for brand mark.
 *
 * Props:
 *  - variant: "mark" | "full" | "wordmark"
 *  - size: "sm" | "md" | "lg"
 *  - onDark: renders light version (for navy backgrounds)
 */
const SIZE = {
  sm: { box: "w-8 h-8", icon: "w-4 h-4", wordmark: "text-sm", tagline: "text-[9px]" },
  md: { box: "w-10 h-10", icon: "w-5 h-5", wordmark: "text-base", tagline: "text-[10px]" },
  lg: { box: "w-14 h-14", icon: "w-7 h-7", wordmark: "text-lg", tagline: "text-[11px]" },
};

export const Logo = ({ variant = "full", size = "md", onDark = false, className = "" }) => {
  const { brand } = useBrand();
  const s = SIZE[size] || SIZE.md;
  const hasImage = !!brand.logo_url;

  const Mark = (
    <div
      className={`${s.box} rounded-xl grid place-items-center overflow-hidden shrink-0 ${
        onDark ? "bg-white" : "bg-brand-navy"
      }`}
      data-testid="brand-logo-mark"
    >
      {hasImage ? (
        <img src={brand.logo_url} alt={brand.name} className="w-full h-full object-cover" />
      ) : (
        <Truck className={`${s.icon} ${onDark ? "text-brand-red" : "text-white"}`} strokeWidth={2.5} />
      )}
    </div>
  );

  if (variant === "mark") return <div className={className}>{Mark}</div>;

  const Wordmark = (
    <div className="min-w-0">
      <div
        className={`font-heading font-bold tracking-tight leading-none truncate ${
          onDark ? "text-white" : "text-brand-navy"
        } ${s.wordmark}`}
      >
        MY CHOICE
      </div>
      <div
        className={`font-mono tracking-[0.28em] mt-1 text-brand-red uppercase ${s.tagline}`}
      >
        MY PORTAL
      </div>
    </div>
  );

  if (variant === "wordmark") return <div className={className}>{Wordmark}</div>;

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {Mark}
      {Wordmark}
    </div>
  );
};

export default Logo;
